import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { getHomePath } from '@/utils/localePaths';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  FileText, 
  Upload,
  CheckCircle,
  AlertCircle,
  Save
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ReportDetailPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { locale } = useTranslation();
  const { token, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [notes, setNotes] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (!user || !user.email || !user.email.toLowerCase().endsWith('@personaajans.com')) {
      toast.error('Bu sayfaya erişim yetkiniz yok');
      navigate(getHomePath(locale));
      return;
    }
    fetchReportDetail();
  }, [reportId, user, navigate]);

  const fetchReportDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/team/pending-report/${reportId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setReport(response.data);
      setNotes(response.data.notes || '');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Rapor detayı yüklenemedi');
      navigate('/bekleyen-raporlar');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip'];
    const allowedExtensions = ['.pdf', '.xlsx', '.zip'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Sadece PDF, XLSX veya ZIP dosyası yükleyebilirsiniz');
      return;
    }

    // Validate file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Dosya boyutu 50MB\'dan küçük olmalıdır');
      return;
    }

    // Upload file
    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/team/upload-report-file/${reportId}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );

      setUploadedFile({
        name: file.name,
        size: file.size,
        url: response.data.file_url
      });
      toast.success('Dosya başarıyla yüklendi!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Dosya yüklenirken hata oluştu');
      setUploadedFile(null);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSaveNotes = async () => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/team/pending-reports/${reportId}/notes`,
        { notes },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      toast.success('Notlar kaydedildi');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Notlar kaydedilemedi');
    }
  };

  const handleCompleteReport = async () => {
    // Validate file upload
    if (!uploadedFile) {
      toast.error('Lütfen önce nihai rapor dosyasını yükleyin');
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmCompleteReport = async () => {
    try {
      setCompleting(true);
      setShowConfirmModal(false);

      const response = await axios.post(
        `${BACKEND_URL}/api/team/complete-report/${reportId}`,
        {
          file_url: uploadedFile.url,
          notes: notes
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      toast.success('Rapor tamamlandı ve kullanıcıya e-posta gönderildi!');
      
      // Redirect to pending reports list
      setTimeout(() => {
        navigate('/bekleyen-raporlar');
      }, 1500);
    } catch (error) {
      console.error('Error completing report:', error);
      toast.error(error.response?.data?.detail || 'Rapor tamamlanırken hata oluştu');
    } finally {
      setCompleting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFDD59] mx-auto mb-4"></div>
          <p className="text-gray-400">Rapor detayı yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-gray-400">Rapor bulunamadı</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/bekleyen-raporlar')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              style={{ fontSize: '14px' }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Bekleyen Raporlar</span>
            </button>
            <h1 className="font-bold text-[#FFDD59]" style={{ fontSize: '20px' }}>Rapor Detayı</h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* User Information */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
          <h2 className="font-bold mb-4 flex items-center gap-2" style={{ fontSize: '17px' }}>
            <User className="w-5 h-5 text-[#FFDD59]" />
            Kullanıcı Bilgileri
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-gray-400 mb-1" style={{ fontSize: '14px' }}>Ad Soyad</p>
              <p className="font-semibold" style={{ fontSize: '15px' }}>{report.user_name}</p>
            </div>
            <div>
              <p className="text-gray-400 mb-1" style={{ fontSize: '14px' }}>E-posta</p>
              <p className="font-semibold flex items-center gap-2" style={{ fontSize: '15px' }}>
                <Mail className="w-4 h-4 text-[#FFDD59]" />
                {report.user_email}
              </p>
            </div>
            <div>
              <p className="text-gray-400 mb-1" style={{ fontSize: '14px' }}>Talep Tarihi</p>
              <p className="font-semibold" style={{ fontSize: '15px' }}>{new Date(report.created_at).toLocaleString('tr-TR')}</p>
            </div>
            <div>
              <p className="text-gray-400 mb-1" style={{ fontSize: '14px' }}>Analiz Hakkı Maliyeti</p>
              <p className="font-semibold" style={{ fontSize: '15px' }}>{report.credit_cost} analiz hakkı</p>
            </div>
          </div>
        </div>

        {/* Influencer List */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
          <h2 className="font-bold mb-4 flex items-center gap-2" style={{ fontSize: '17px' }}>
            <FileText className="w-5 h-5 text-[#FFDD59]" />
            İstenen Influencer Listesi ({report.influencer_count} kişi)
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left py-3 px-4 text-gray-400 font-semibold" style={{ fontSize: '14px' }}>#</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-semibold" style={{ fontSize: '14px' }}>Kullanıcı Adı</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-semibold" style={{ fontSize: '14px' }}>Ödeme Bilgisi</th>
                </tr>
              </thead>
              <tbody>
                {report.influencers.map((influencer, index) => (
                  <tr key={index} className="border-b border-gray-800 hover:bg-gray-800/50">
                    <td className="py-3 px-4 text-gray-400" style={{ fontSize: '14px' }}>{index + 1}</td>
                    <td className="py-3 px-4 font-semibold" style={{ fontSize: '15px' }}>
                      {influencer.username}
                    </td>
                    <td className="py-3 px-4" style={{ fontSize: '15px' }}>
                      {influencer.payment ? (
                        <span className="text-green-400">₺{influencer.payment.toLocaleString('tr-TR')}</span>
                      ) : (
                        <span className="text-gray-500">Belirtilmemiş</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes Section */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
          <h2 className="font-bold mb-4" style={{ fontSize: '17px' }}>İç Notlar</h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Rapor işleme sürecinde eklemek istediğiniz notları buraya yazabilirsiniz..."
            className="w-full bg-black border border-gray-800 rounded-lg p-4 text-white placeholder-gray-500 focus:outline-none focus:border-[#FFDD59] min-h-[150px] resize-y"
            style={{ fontSize: '14px' }}
          />
          <button
            onClick={handleSaveNotes}
            className="mt-3 flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
            style={{ fontSize: '14px' }}
          >
            <Save className="w-4 h-4" />
            Notları Kaydet
          </button>
        </div>

        {/* File Upload Section */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
          <h2 className="font-bold mb-4 flex items-center gap-2" style={{ fontSize: '17px' }}>
            <Upload className="w-5 h-5 text-[#FFDD59]" />
            Nihai Rapor Dosyası
          </h2>
          
          <div className="mb-4">
            <label className="block mb-2 text-gray-400" style={{ fontSize: '14px' }}>
              Hazırladığınız rapor dosyasını (PDF, XLSX veya ZIP) yükleyin
            </label>
            <input
              type="file"
              accept=".pdf,.xlsx,.zip"
              onChange={handleFileChange}
              disabled={uploading}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className={`flex items-center justify-center gap-2 border-2 border-dashed ${
                uploadedFile ? 'border-green-500 bg-green-500/10' : 'border-gray-700 hover:border-[#FFDD59]'
              } rounded-lg p-8 cursor-pointer transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {uploading ? (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FFDD59] mx-auto mb-2"></div>
                  <p className="text-gray-400" style={{ fontSize: '14px' }}>Yükleniyor... {uploadProgress}%</p>
                </div>
              ) : uploadedFile ? (
                <div className="text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="font-semibold text-green-400" style={{ fontSize: '15px' }}>{uploadedFile.name}</p>
                  <p className="text-gray-400" style={{ fontSize: '14px' }}>{formatFileSize(uploadedFile.size)}</p>
                  <p className="text-gray-500 mt-2" style={{ fontSize: '13px' }}>Yeni dosya yüklemek için tıklayın</p>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-400" style={{ fontSize: '14px' }}>Dosya seçmek için tıklayın veya sürükleyin</p>
                  <p className="text-gray-500 mt-1" style={{ fontSize: '13px' }}>PDF, XLSX veya ZIP (Max 50MB)</p>
                </div>
              )}
            </label>
          </div>

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="w-full bg-gray-800 rounded-full h-2 mb-4">
              <div 
                className="bg-[#FFDD59] h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          )}
        </div>

        {/* Complete Button */}
        <div className="bg-gradient-to-r from-green-900/30 to-blue-900/30 border border-green-700 rounded-lg p-6">
          <div className="flex items-start gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div className="text-green-200" style={{ fontSize: '14px' }}>
              <p className="font-semibold mb-1">Son Adım</p>
              <p>
                Raporu tamamladığınızda, sistem otomatik olarak kullanıcıya e-posta gönderecek ve 
                raporu bekleyen listeden kaldıracaktır.
              </p>
            </div>
          </div>
          
          <button
            onClick={handleCompleteReport}
            disabled={!uploadedFile || completing}
            className={`w-full flex items-center justify-center gap-2 font-bold py-4 px-6 rounded-lg transition-all ${
              uploadedFile && !completing
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
            style={{ fontSize: '15px' }}
          >
            {completing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                İşleniyor...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Raporu Tamamla ve Kullanıcıya Gönder →
              </>
            )}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Raporu Tamamla</h3>
            <p className="text-gray-300 mb-6">
              Nihai rapor dosyası yüklendi. Bu işlemi onayladığınızda:
            </p>
            <ul className="list-disc list-inside text-gray-300 mb-6 space-y-2">
              <li>Rapor durumu "Tamamlandı" olarak güncellenecek</li>
              <li>Kullanıcıya otomatik e-posta gönderilecek</li>
              <li>Rapor bekleyen listeden kaldırılacak</li>
            </ul>
            <p className="text-yellow-400 text-sm mb-6">
              Bu işlem geri alınamaz. Onaylıyor musunuz?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-white py-3 rounded-lg transition-colors"
              >
                İptal
              </button>
              <button
                onClick={confirmCompleteReport}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg transition-colors font-semibold"
              >
                Onayla ve Gönder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportDetailPage;
